import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, EntitiesService, GenesisUnitOfWork, ReportService, SessionInformationService, UtilityService } from 'app/core/core.module';
import { HistoryService } from 'app/core/history.service';
import { AssemblySchedule, Dealer, Invoice, Order, OrderLineDetail, OrderType, PackingSlip } from 'app/core/model/entity-model';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { DxDataGridComponent } from 'devextreme-angular';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { alert, confirm } from 'devextreme/ui/dialog';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { MsoDialogComponent } from '../mso-dialog.component';

@Component({
    selector: 'app-order-history',
    templateUrl: './order-history.component.html',
    styleUrls: ['./order-history.component.scss'],
    standalone: false,
})
export class OrderHistoryComponent implements OnInit {
    orderHistory: any[];
    filter: any;
    dealers: Dealer[];
    types: OrderType[];
    canAllocateStock: boolean = false;
    canUpdateStatus: boolean = false;
    canStartWarranty: boolean = false;
    canAdjustBuiltAndDeliveredDates: boolean = false;

    showAsDialog: boolean = false;
    result: Subject<any> = new Subject<any>();

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;

    constructor(
        private uow: GenesisUnitOfWork,
        public auth: AuthService,
        private sessionInfo: SessionInformationService,
        private reportService: ReportService,
        private appInfo: AppInformationService,
        private modalService: BsModalService,
        private utility: UtilityService,
        private entitiesService: EntitiesService,
        private router: Router,
        private historyService: HistoryService,
    ) {}

    async ngOnInit() {
        this.types = this.uow.lookups.orderTypes;
        this.dealers = (await this.uow.getDealers(true)) as Dealer[];
        const filter = this.sessionInfo.getItem('order-history-filter');
        if (filter && !this.showAsDialog) {
            this.filter = filter;
            // await this.search();
        } else this.filter = {};

        this.canAllocateStock = this.auth.hasRole('BuildDetail:AllocateStock');
        this.canUpdateStatus = this.auth.hasRole('SalesOrder:UpdateStatus');
        this.canStartWarranty = this.auth.hasRole('SalesOrder:CanStartWarranty');
        this.canAdjustBuiltAndDeliveredDates = this.auth.hasRole('BuildDetail:CanAdjustBuiltAndDeliveredDate');
    }

    async search() {
        this.setDetailIds();
        if (
            !this.filter.typeId &&
            !this.filter.dealerId &&
            !this.filter.salesOrderNumber &&
            !this.filter.partNumber &&
            this.filter.detailIds.length === 0 &&
            !this.filter.buildStart &&
            !this.filter.buildEnd &&
            !this.filter.orderStart &&
            !this.filter.orderEnd &&
            !this.filter.vin &&
            !this.filter.packingSlipNumber
        ) {
            const result = await confirm(`No filters set.  Are you sure you want to continue?`, `${this.appInfo.settings.company}`);
            if (!result) return;
        }

        this.appInfo.pleaseWaitShow();
        const parentId = await this.getParentId();
        if (parentId) {
            this.appInfo.pleaseWaitHide();
            const result = await confirm('The detail id is an option. Would you like to search for the parent detail instead?', this.appInfo.settings.company);
            if (result) this.filter.detailIds = [parentId];
            this.appInfo.pleaseWaitShow();
        }
        this.sessionInfo.setItem('order-history-filter', this.filter);
        this.orderHistory = await this.historyService.getOrderHistory(this.filter);
        this.appInfo.pleaseWaitHide();
    }

    setDetailIds() {
        let detailIds = [];
        if (this.filter.orderLineDetailField) {
            const detailField = this.filter.orderLineDetailField as string;
            if (detailField.includes(',')) detailIds = detailField.split(',');
            else detailIds = [detailField];
        }
        // filters out blank and non-numeric values
        this.filter.detailIds = detailIds.map((s) => s.trim()).filter((s) => s && !isNaN(Number(s)));
    }

    async getParentId() {
        if (this.filter.detailIds.length !== 1) return;
        const detail = (await this.uow.orderLineDetails.byId(this.filter.detailIds[0])) as OrderLineDetail;
        return detail?.parentLineId;
    }

    clear() {
        this.filter = {};
        this.sessionInfo.setItem('order-history-filter', this.filter);
    }

    // this method is used to clear the selection when the user searches for something
    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            this.dataGrid.instance.clearSelection();
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        if (e.component.__searchChanged) {
            //__searchChanged is for the search box
            e.component.__searchChanged = false;
            this.dataGrid.instance.clearSelection();
        }
    }

    async rowPrepare(e) {
        if (e.rowType === 'data' && !!e.data.dealerColorCode) {
            if (e.data.dealerColorCode === 'blue') e.rowElement.className = e.rowElement.className + ' blue-line';
            else if (e.data.dealerColorCode === 'red') e.rowElement.className = e.rowElement.className + ' red-line';
            else if (e.data.dealerColorCode === 'green') e.rowElement.className = e.rowElement.className + ' green-line';
            else if (e.data.dealerColorCode === 'orange') e.rowElement.className = e.rowElement.className + ' orange-line';
            else if (e.data.dealerColorCode === 'white') e.rowElement.className = e.rowElement.className + ' white-line';
            else if (e.data.dealerColorCode === 'gray') e.rowElement.className = e.rowElement.className + ' gray-line';
        }
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    async onExporting(e) {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Main sheet');
        await exportDataGrid({
            component: e.component,
            worksheet: worksheet,
            customizeCell: ({ gridCell, excelCell }) => {
                excelCell.font = { name: 'Arial', size: 12 };
                excelCell.alignment = { horizontal: 'left' };
                if (gridCell.rowType !== 'data' || !gridCell.column) return;
                if (gridCell.column.dataField === 'optionHTML') {
                    excelCell.value = gridCell.data.optionExport;
                    excelCell.alignment = { wrapText: true };
                }
                if (gridCell.column.dataField === 'noteHTML') {
                    excelCell.value = gridCell.data.noteExport;
                    excelCell.alignment = { wrapText: true };
                }
            },
        });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
    }

    async rowDoubleClick(e) {}

    async printPDF(cell: any) {
        if (cell.column.dataField === 'slipNumber') {
            const reportGuid = ((await this.uow.getPackSlipByNumber(cell.value)) as PackingSlip).reportGuid;
            this.reportService.openPackingSlip([reportGuid]);
        }
        if (cell.column.dataField === 'invoiceNumber') {
            const reportGuid = ((await this.uow.getInvoiceByInvoiceNumber(cell.value)) as Invoice).reportGuid;
            this.reportService.openInvoiceReport([reportGuid]);
        }
    }

    async addToStock() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        const result = await confirm(`Are you sure you want to add this detail to Genesis Stock?`, `${this.appInfo.settings.company}`);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        const allocated = (await this.utility.addToStock(selectedRow.id, this.auth.currentUser.id)) as any;
        this.appInfo.pleaseWaitHide();
    }

    async updateStatus(rows: any) {
        if (!rows) return;
        const detailIds = rows.map((row) => row.id);
        const details = (await this.uow.orderLineDetails.byIds(detailIds, 'optionDetails')) as any as OrderLineDetail[];

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Update Status',
                dialogPrompt: 'Select Status',
                mode: 'selectWithId',
                dialogValueArray: this.uow.lookups.orderLineStatuses,
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.result && result.value) {
                this.appInfo.pleaseWaitShow();
                let status = null;
                Object.keys(OrderLineDetail.Statuses).forEach((key) => {
                    if (OrderLineDetail.Statuses[key] === result.value) status = key;
                });

                details.forEach((detail) => {
                    if (detail.statusId === result.value) return;

                    detail.statusId = result.value;
                    this.entitiesService.createOrderLineDetailNote(detail.id, `Status Changed to ${status}`, this.auth.currentUser.id);
                    if (result.value === OrderLineDetail.Statuses.DeliveredPickedUp && !detail.delivered) detail.delivered = new Date();

                    detail.optionDetails.forEach((option) => {
                        option.statusId = result.value;
                        if (result.value === OrderLineDetail.Statuses.DeliveredPickedUp && !option.delivered) option.delivered = new Date();
                    });
                });
                await this.uow.commit();
                await this.search();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async addNote() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Build Note',
                dialogPrompt: 'Enter Note',
                checkNumeric: false,
                checkInteger: false,
                mode: 'text',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                this.appInfo.pleaseWaitShow();
                await this.entitiesService.addNote(selectedRows, result.value, true);
                await this.uow.commit();
                await this.search();
                this.appInfo.pleaseWaitHide();
                this.dataGrid.instance.clearSelection();
            }
            modalRef.hide();
        });
    }

    async manualInventoryUpdate() {
        // used for troubleshooting
        // const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        // if (!selectedRows) return;
        // this.appInfo.pleaseWaitShow();
        // await this.utility.manualUpdateInventory(selectedRows[0].id);
        // this.appInfo.pleaseWaitHide();
    }

    async newWarranty() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const row = selectedRows[0];
        console.log(row);
        if (!row.built) {
            await alert(`Trailer is not built, cannot start a warranty`, `${this.appInfo.settings.company}`);
            return;
        }

        const order = (await this.uow.orders.byId(row.orderId)) as Order;
        if (order.typeId === Order.Types.Warranty) {
            await alert(`Selected line is already a warranty`, `${this.appInfo.settings.company}`);
            return;
        }
        const queryParams = { originalOrderId: order.id, warranty: true, vin: row.vin };
        this.router.navigate(['/sales-orders/new'], { queryParams });
    }

    async printBuildList() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows || selectedRows.length === 0) return;

        const ids = selectedRows.map((x) => {
            return x.id;
        });
        ids.forEach((id) => {
            const schedule = this.uow.assemblySchedules.createEntity() as AssemblySchedule;
            schedule.buildDate = new Date();
            schedule.created = new Date();
            schedule.createdBy = this.auth.currentUser.userName;
            schedule.jigId = 1;
            schedule.orderLineDetailId = id;
        });
        await this.uow.commit();
        await this.reportService.printAssemblySchedule({ ids: ids });
    }

    printMsrp(orderLineDetail: any) {
        if (!orderLineDetail) return;

        this.reportService.openMsrpReport([orderLineDetail.id]);
    }

    saveDialog() {
        if (this.showAsDialog && !!this.dataGrid) {
            const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
            if (!selectedRow) return;
            this.result.next({ success: true, orderLineDetailId: selectedRow.id });
        }
    }

    closeDialog() {
        if (this.showAsDialog) this.result.next({});
    }

    async adjustBuiltDate() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        const detail = (await this.uow.orderLineDetails.byId(selectedRow.id, 'optionDetails')) as OrderLineDetail;
        if (detail.optionDetails.some((option) => !option.deleted && !option.built)) {
            await alert(`One or more options do not have a built value. Please ensure all options are built before proceeding.`, `${this.appInfo.settings.company}`);
            return;
        }
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Adjust Built Date',
                dialogPrompt: 'Enter New Built Date',
                dialogValue: detail.built,
                checkNumeric: false,
                checkInteger: false,
                mode: 'date',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                this.appInfo.pleaseWaitShow();
                detail.built = result.value;
                detail.scheduled = result.value;
                detail.optionDetails.forEach((option) => {
                    option.built = result.value;
                    option.scheduled = result.value;
                });
                if (!detail.assemblyJigId) detail.assemblyJigId = 1;
                await this.uow.commit();
                await this.search();
                this.appInfo.pleaseWaitHide();
                this.dataGrid.instance.clearSelection();
            }
            modalRef.hide();
        });
    }

    async adjustDeliveredDate() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        const detail = (await this.uow.orderLineDetails.byId(selectedRow.id, 'optionDetails')) as OrderLineDetail;
        if (!detail.delivered || detail.optionDetails.some((option) => !option.deleted && !option.delivered)) {
            await alert(`One or more options do not have a delivered value. Please ensure all options are delivered before proceeding.`, `${this.appInfo.settings.company}`);
            return;
        }
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Adjust Date',
                dialogPrompt: 'Enter New Delivered Date',
                dialogValue: detail.delivered,
                checkNumeric: false,
                checkInteger: false,
                mode: 'date',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                this.appInfo.pleaseWaitShow();
                detail.delivered = result.value;
                detail.optionDetails.forEach((option) => {
                    option.delivered = result.value;
                });
                await this.uow.commit();
                await this.search();
                this.appInfo.pleaseWaitHide();
                this.dataGrid.instance.clearSelection();
            }
            modalRef.hide();
        });
    }

    async msoMailed() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows || selectedRows.length === 0) return;

        //open the input dialog to get a note if they want to add one
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'MSO Mailed',
                dialogPrompt: 'Enter Note (optional)',
                checkNumeric: false,
                checkInteger: false,
                mode: 'textarea',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result) {
                this.appInfo.pleaseWaitShow();
                // Handle the note if provided
                selectedRows.forEach((row) => {
                    this.entitiesService.createOrderLineDetailNote(row.id, `MSO Mailed`, this.auth.currentUser.id);
                    if (result.value) {
                        this.entitiesService.createOrderLineDetailNote(row.id, `${result.value}`, this.auth.currentUser.id, 1, true);
                    }
                });
                await this.uow.commit();
                await this.search();
                this.appInfo.pleaseWaitHide();
                this.dataGrid.instance.clearSelection();
            }
            modalRef.hide();
        });
    }

    async duplicateMSO() {
        // this.appInfo.notifyToast('This feature is not currently available. Please contact support if you need this functionality.', 'info');
        // return;

        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows || selectedRows.length === 0) return;

        //open the input dialog to get a note if they want to add one
        const modalRef = this.modalService.show(MsoDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dealerName: selectedRows[0].dealer,
                vins: selectedRows.map((row) => row.vin),
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && result.value) {
                this.appInfo.pleaseWaitShow();

                const createMsoOrderResult = await this.utility.createMsoOrder(
                    selectedRows[0].dealerId,
                    result.value.price,
                    selectedRows.map((row) => row.vin),
                    this.auth.currentUser.id,
                );

                selectedRows.forEach((row) => {
                    this.entitiesService.createOrderLineDetailNote(row.id, `MSO Order Created`, this.auth.currentUser.id);
                });
                if (!!result.value.notes) {
                    selectedRows.forEach((row) => {
                        this.entitiesService.createOrderLineDetailNote(row.id, `${result.value.notes}`, this.auth.currentUser.id, 1, true);
                    });
                }

                await this.uow.commit();
                await this.search();
                await this.utility.processOrderLineDetails(createMsoOrderResult.OrderLineDetailIds);
                console.log(createMsoOrderResult);
                const order = (await this.uow.orders.byId(createMsoOrderResult.OrderId)) as Order;
                const invoice = (await this.uow.invoices.byId(createMsoOrderResult.InvoiceId)) as Invoice;
                const packingSlip = (await this.uow.packingSlips.byId(createMsoOrderResult.PackingSlipId)) as PackingSlip;
                this.appInfo.pleaseWaitHide();
                this.dataGrid.instance.clearSelection();

                alert(`MSO Order#: ${order.invoiceNumber} created successfully.  Invoice #: ${invoice.invoiceNumber}`, `${this.appInfo.settings.company}`);

                this.reportService.openSalesReport([order.orderGuid]);
                this.reportService.openPackingSlip([packingSlip.reportGuid]);
                this.reportService.openInvoiceReport([invoice.reportGuid]);
            }
            modalRef.hide();
        });
    }
}
